import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import config from "./config";
import { getAppearancePreference } from "./plansAppearance";
import "./Settings.css";
import "./AccountDeletion.css";

const API = config.API_URL.replace(/\/+$/, "");

export default function AccountDeletion({ cancellation = false, onDeleted }) {
  const [token] = useState(() => cancellation ? new URLSearchParams(window.location.hash.slice(1)).get("token") || "" : "");
  const [account, setAccount] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    // Keep the bearer link out of subsequent navigation/referrers. It stays only
    // in component memory; refresh requires reopening the original email link.
    if (cancellation && window.location.hash) window.history.replaceState(null, "", window.location.pathname + window.location.search);
    const controller = new AbortController();
    let current = true;
    if (cancellation && !/^[A-Za-z0-9_-]{43}$/.test(token)) {
      setError("This cancellation link is invalid or has expired. Reopen the link in your welcome email.");
      setLoading(false); return () => controller.abort();
    }
    const bearer = localStorage.getItem("token");
    if (!cancellation && !bearer) {
      setError("Sign in to manage your account, then return to Settings."); setLoading(false);
      return () => controller.abort();
    }
    fetch(`${API}/api/account/${cancellation ? "cancellation/details" : "deletion"}`, {
      method: cancellation ? "POST" : "GET", credentials: "omit", signal: controller.signal,
      headers: cancellation ? { "Content-Type": "application/json" } : { Authorization: `Bearer ${bearer}` },
      ...(cancellation ? { body: JSON.stringify({ token }) } : {}),
    }).then(async response => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || "Account details could not be loaded.");
      if (!Number.isInteger(body.userId) || typeof body.email !== "string") throw new Error("Account details could not be loaded.");
      if (current) setAccount(body);
    }).catch(reason => { if (current && reason.name !== "AbortError") setError(reason.message || "Account details could not be loaded."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [cancellation, token]);

  async function removeAccount(event) {
    event.preventDefault();
    if (!account || !confirmed || deleting) return;
    setDeleting(true); setError("");
    try {
      const response = await fetch(`${API}/api/account/${cancellation ? "cancellation" : "deletion"}`, {
        method: "DELETE", credentials: "omit",
        headers: { "Content-Type": "application/json", ...(!cancellation ? { Authorization: `Bearer ${localStorage.getItem("token")}` } : {}) },
        body: JSON.stringify({ confirm: true, ...(cancellation ? { token } : {}) }),
      });
      const result = await response.json();
      if (!response.ok || result.status !== "success") throw new Error(result.message || "The account could not be deleted. Please try again.");
      setDone(true);
      onDeleted?.(account.userId);
    } catch (reason) { setError(reason.message || "The account could not be deleted. Please try again."); }
    finally { setDeleting(false); }
  }

  const created = account?.createdAtUtc ? new Date(account.createdAtUtc) : null;
  return <main className={`settings-page account-deletion${getAppearancePreference() === "light" ? " settings-page--light" : ""}`}>
    <div className="settings-inner">
      <header className="settings-header"><p className="settings-eyebrow">YOUR SIGNALTUNER ACCOUNT</p>
        <h1>{done ? "Account deleted" : cancellation ? "Review this account" : "Delete your account"}</h1>
      </header>
      <section className="settings-card" aria-busy={loading || deleting}>
        {done ? <><p role="status">The account has been deleted. This cannot be undone.</p><Link to="/home">Return to SignalTuner</Link></>
          : <>
            {loading && <p role="status">Loading account details…</p>}
            {error && <p role="alert">{error}</p>}
            {account && <>
              <h2>Account details</h2>
              <dl><dt>Name</dt><dd>{account.name || "Not provided"}</dd><dt>Email address</dt><dd>{account.email}</dd>
                <dt>Created</dt><dd>{created && !Number.isNaN(created.getTime()) ? <time dateTime={account.createdAtUtc}>{created.toLocaleString(undefined, { dateStyle: "long", timeStyle: "long" })}</time> : "Not recorded for this account"}</dd>
              </dl>
              <p>{cancellation ? "If you did not create this account, you can delete it below. Viewing this page does not change the account." : "Review the details before you permanently delete your account."}</p>
              <p>Deletion removes your account and its associated records. Other users’ history and device-only logs from shared computers may remain. Billing records, active analyses or teams with other members or remaining team credits may require attention first.</p>
              <form onSubmit={removeAccount}>
                <label className="account-deletion-confirm"><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={deleting} />
                  I understand that deleting this account is permanent.</label>
                <div className="account-deletion-actions"><button className="settings-button account-deletion-button" type="submit" disabled={!confirmed || deleting}>{deleting ? "Deleting account…" : "Delete account permanently"}</button>
                  <Link to={cancellation ? "/home" : "/settings"}>{cancellation ? "Keep this account" : "Back to settings"}</Link></div>
              </form>
            </>}
            {!loading && <p><Link to="/support">Contact support</Link>{!account && <> · <Link to="/home">Sign in</Link></>}</p>}
          </>}
      </section>
    </div>
  </main>;
}
